import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Calendar, GasPump as Fuel, Gear as Cog, Gauge, Gear as Settings, ShieldCheck, CurrencyDollar as DollarSign } from '@phosphor-icons/react';
import { useDutyFree, calculateDutyFreePrice } from '../context/DutyFreeContext';
import QRColumn from './print/QRColumn';
import NewModelPrintTemplate from './print/NewModelPrintTemplate';
import { buildLandingUrl } from './print/utils/landingUrl';
import { formatRegYear } from '../utils/registrationYear';
import { t as tPrint } from './print/translations';

/** Default/min/step sizes for the title (px). */
const TITLE_MAX_SIZE = 40;
const TITLE_MIN_SIZE = 24;
const TITLE_STEP = 1;
const TITLE_MAX_LINES = 2;

/** Used-VIN QR is rounded and slightly smaller than the new-model variant. */
const USED_VIN_QR_SIZE = 360;
const USED_VIN_QR_SHAPE = 'rounded';

/** QR-only mode (issue #949): большой центрированный QR на всю короткую
 * сторону A4 landscape минус padding. A4 landscape = 842×595 px. 400px
 * оставляет ~30px Powered text снизу + 1.5cm crop-marks padding со всех
 * сторон — все 4 угловые метки помещаются в landscape без overflow. */
const QR_ONLY_SIZE = 400;

const formatPrice = (price, lang) => {
  if (!price || price === 0) return tPrint('contact', lang);
  return `€${price.toLocaleString()}`;
};

const formatMileage = (mileage) => {
  if (!mileage || mileage === 0) return 'N/A';
  return `${mileage.toLocaleString()} km`;
};

/**
 * CarPrintTemplate — visual content of one A4 print page.
 *
 * Used both on the single-print page (`/cars/:id/print`) and on the
 * bulk-print page (`/cars/print-bulk?ids=...`). On bulk it renders
 * multiple blocks one after another with `break-after: page`. Kept
 * separate from `CarPrintPage` so the markup is not duplicated.
 *
 * Does NOT contain: print action button (that belongs to page chrome),
 * routing, data fetch. Accepts a ready `car` object.
 *
 * Acts as a dispatcher between the used-VIN layout (this file) and the
 * new-model showroom layout (`./print/NewModelPrintTemplate`). Once the
 * dispatcher branches, neither side references `isNewModel` again.
 *
 * @param {object} props
 * @param {object} props.car — DealerCarDto (requires make/model/year/price/...)
 * @param {() => void} [props.onQrReady] — fired when the QR is fully
 *   appended to the DOM. Bulk-print uses this to throttle window.print().
 */
const CarPrintTemplate = ({ car, mode = 'full', orientation = 'horizontal', language = 'fr', onQrReady }) => {
  // Dispatch BEFORE running used-VIN-specific hooks. Hook count must stay
  // stable across renders, but `car` is the same identity for the whole
  // mounted lifetime of this component (each car has its own bulk page),
  // so it is safe to early-return on this branch.
  // qr-only и catalog-qr используют тот же минималистичный layout — большой
  // центрированный QR без specs/cards/price. catalog-qr только меняет URL
  // QR-кода (см. `buildLandingUrl(car, 'catalog-qr')` → `${window.location.origin}/#/salon`,
  // т.е. salon Welcome на самом demo-домене, а не localhost. (#1060)).
  if (mode === 'qr-only' || mode === 'catalog-qr') {
    return <QrOnlyPrintTemplate car={car} orientation={orientation} language={language} mode={mode} onQrReady={onQrReady} />;
  }
  if (car?.isNewModel) {
    return <NewModelPrintTemplate car={car} orientation={orientation} language={language} onQrReady={onQrReady} />;
  }
  return <UsedVinPrintTemplate car={car} orientation={orientation} language={language} onQrReady={onQrReady} />;
};

/**
 * QR-only layout (issue #949) — большой центрированный QR без specs/cards/
 * price. Используется для печати минималистичных наклеек под стекло, когда
 * вся текстовая инфа уже есть на машине или дилеру не нужна.
 */
const QrOnlyPrintTemplate = ({ car, orientation = 'horizontal', language = 'fr', mode = 'qr-only', onQrReady }) => {
  const landingUrl = useMemo(() => buildLandingUrl(car, mode), [car, mode]);
  // Шейп QR такой же, какой бы получился в Full режиме: 'sharp' для
  // new-model (showroom-эстетика Autodis, issue #884), 'rounded' для
  // used-VIN. Чтобы оба превью смотрелись одинаково при переключении
  // тоггла, не теряя брендового различия между линейками.
  const shape = car?.isNewModel ? 'sharp' : 'rounded';

  if (!car) return null;

  const orientationClass = orientation === 'vertical' ? ' print-template--vertical' : '';

  return (
    <div className={`print-template print-template--qr-only${orientationClass}`}>
      {/* Crop-zone — обёртка вокруг QR. Когда включены cut marks,
          получает padding ~2cm и background-image с 4 угловыми L-метками
          (см. .print-template-render--cut-marks rules в CarPrintPage.css).
          Без cut marks остаётся плотно вокруг QR. */}
      <div className="print-template__crop-zone">
        <QRColumn
          url={landingUrl}
          size={QR_ONLY_SIZE}
          shape={shape}
          language={language}
          onReady={onQrReady}
        />
      </div>
    </div>
  );
};

/**
 * Used-VIN layout — title, 2 spec columns, Warranty + Financing cards,
 * 3-shape price section with regular + duty-free prices and footnotes.
 */
const UsedVinPrintTemplate = ({ car, orientation = 'horizontal', language = 'fr', onQrReady }) => {
  const FALLBACK_VALUE = tPrint('fallback', language);
  const DEFAULT_WARRANTY = tPrint('defaultWarranty', language);
  const { calculateDutyFree, vatRate, govFeeText } = useDutyFree();
  const titleRef = useRef(null);
  const [titleSize, setTitleSize] = useState(TITLE_MAX_SIZE);

  const cleanMake = car?.make?.replace(/\*/g, '') || car?.make || '';
  const cleanModel = car?.model?.replace(/\*/g, '') || car?.model || '';
  const displayTitle = car?.name || `${cleanMake} ${cleanModel}`.trim();

  const regularPrice = car?.price;
  const inventoryDutyFree = car ? calculateDutyFree(car) : null;
  const dutyFreePrice = (inventoryDutyFree && inventoryDutyFree !== regularPrice)
    ? inventoryDutyFree
    : (regularPrice ? calculateDutyFreePrice(regularPrice, vatRate) : null);

  // QR landing URL building moved to print/utils/landingUrl.js.
  // Logic supports both used-VIN dealer-site flow and new-model standalone
  // landing — see buildLandingUrl docstring (issues #878 / #897 / #884).
  const landingUrl = useMemo(() => buildLandingUrl(car), [car]);

  // Auto-fit title into 2 lines. useLayoutEffect — synchronous before
  // paint to avoid flicker.
  useLayoutEffect(() => {
    const el = titleRef.current;
    if (!el || !displayTitle) return;

    const measureLines = () => el.getClientRects().length;

    let size = TITLE_MAX_SIZE;
    el.style.fontSize = `${size}px`;

    if (measureLines() <= TITLE_MAX_LINES) {
      el.style.fontSize = '';
      setTitleSize(TITLE_MAX_SIZE);
      return;
    }

    while (measureLines() > TITLE_MAX_LINES && size > TITLE_MIN_SIZE) {
      size -= TITLE_STEP;
      el.style.fontSize = `${size}px`;
    }

    setTitleSize(size);
    el.style.fontSize = '';
  }, [displayTitle]);

  if (!car) return null;

  const orientationClass = orientation === 'vertical' ? ' print-template--vertical' : '';

  return (
    <div className={`print-template${orientationClass}`}>
      <div className="print-template__content">
        <QRColumn
          url={landingUrl}
          size={USED_VIN_QR_SIZE}
          shape={USED_VIN_QR_SHAPE}
          language={language}
          onReady={onQrReady}
        />

        <div className="print-template__info-col">
          <h1
            ref={titleRef}
            className="print-template__title"
            style={{ fontSize: `${titleSize}px` }}
          >
            {displayTitle}
          </h1>

          <div className="print-template__specs">
            <div className="print-template__specs-col">
              {/* Год возвращён для used-VIN (поддержанные авто, Andys): #1021
                  убрал, но контекст был про new-model (Autodis). У б/у машин
                  год важен. (#1064) */}
              <div className="print-template__spec">
                <Calendar className="print-template__spec-icon" weight="light" />
                <span className="print-template__spec-value print-template__spec-value--first">
                  {formatRegYear(car, FALLBACK_VALUE)}
                </span>
              </div>
              <div className="print-template__spec">
                <Gauge className="print-template__spec-icon" weight="light" />
                <span className="print-template__spec-value">{formatMileage(car.mileage)}</span>
              </div>
              {car.engineDisplacement && (
                <div className="print-template__spec">
                  <Settings className="print-template__spec-icon" weight="light" />
                  <span className="print-template__spec-value">{car.engineDisplacement}</span>
                </div>
              )}
            </div>
            <div className="print-template__specs-col">
              <div className="print-template__spec">
                <Cog className="print-template__spec-icon" weight="light" />
                <span className="print-template__spec-value">{car.transmission || FALLBACK_VALUE}</span>
              </div>
              <div className="print-template__spec">
                <Fuel className="print-template__spec-icon" weight="light" />
                <span className="print-template__spec-value">{car.fuelType || FALLBACK_VALUE}</span>
              </div>
            </div>
          </div>

          <div className="print-template__cards">
            <div className="print-template__card">
              <div className="print-template__card-top">
                <ShieldCheck className="print-template__card-icon" weight="light" />
                <span className="print-template__card-label">Warranty</span>
              </div>
              <span className="print-template__card-value">
                {car.warrantyTerm || DEFAULT_WARRANTY}
              </span>
            </div>
            <div className="print-template__card">
              <div className="print-template__card-top">
                <DollarSign className="print-template__card-icon" weight="light" />
                <span className="print-template__card-label">Financing</span>
              </div>
              <div className="print-template__card-details">
                <span className="print-template__card-detail">Deposit 25%</span>
                <span className="print-template__card-detail">Up to 5 years repayment</span>
                <span className="print-template__card-detail">Low interest rate</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="print-template__price-section">
        <div className="print-template__price-shapes">
          <div className="print-template__price-shape print-template__price-shape--red">
            <svg
              className="print-template__price-shape-bg"
              viewBox="0 0 298 84"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              <path
                d="M8 0 H282 L298 42 L282 84 H8 A8 8 0 0 1 0 76 V8 A8 8 0 0 1 8 0 Z"
                fill="currentColor"
              />
            </svg>
            <div className="print-template__price-shape-content">
              <span className="print-template__price-amount">{formatPrice(regularPrice)}</span>
              <span className="print-template__price-reg">+reg *</span>
            </div>
          </div>

          <div className="print-template__price-shape print-template__price-shape--dashed1">
            {/* Inline SVG: <img src="/assets/...">  ломается в widget-overlay
                режиме, потому что путь резолвится против host origin
                (dev.eezyworks.com) — там этих ассетов нет → 404. Inline
                согласован с --red shape выше. preserveAspectRatio default
                ("xMidYMid meet") — не "none" как у --red — чтобы при
                возможном будущем дрейфе CSS-размеров stroke-width не
                растягивался неравномерно (форма stroked, в отличие от
                залитой --red). */}
            <svg className="print-template__price-shape-bg" viewBox="0 0 217 84" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M8 1 H201 L216 42 L201 83 H8 A7 7 0 0 1 1 76 L17 42 L1 8 A7 7 0 0 1 8 1 Z" stroke="black" strokeWidth="2" strokeDasharray="3 3" strokeLinejoin="round" fill="none"/>
            </svg>
            <div className="print-template__price-shape-content">
              <span className="print-template__price-dutyfree-label">
                BRITISH FORCES<br />DUTY FREE PRICE
              </span>
            </div>
          </div>

          <div className="print-template__price-shape print-template__price-shape--dashed2">
            <svg className="print-template__price-shape-bg" viewBox="0 0 254 84" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M8 1 H246 A7 7 0 0 1 253 8 V76 A7 7 0 0 1 246 83 H8 A7 7 0 0 1 1 76 L17 42 L1 8 A7 7 0 0 1 8 1 Z" stroke="black" strokeWidth="2" strokeDasharray="3 3" strokeLinejoin="round" fill="none"/>
            </svg>
            <div className="print-template__price-shape-content">
              <span className="print-template__price-dutyfree-amount">
                {dutyFreePrice ? formatPrice(dutyFreePrice) : formatPrice(regularPrice)}
                <span className="print-template__price-dutyfree-marker">**</span>
              </span>
              <span className="print-template__price-dutyfree-reg">+reg *</span>
            </div>
          </div>
        </div>

        <div className="print-template__footnotes">
          <span className="print-template__footnote">{govFeeText}</span>
          <span className="print-template__footnote">** For British Forces/Serving Personnel</span>
          <span className="print-template__footnote print-template__footnote--id">
            ID: {car.referenceNumber || car.id}
          </span>
          {car.vin && (
            <span className="print-template__footnote print-template__footnote--id">
              Chassis ID: {car.vin}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default CarPrintTemplate;
